import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, LibraryBig, UserPlus, BookOpenText, Languages } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Ticker } from "@/components/Ticker";
import { Footer } from "@/components/Footer";
import { MaskLines } from "@/components/Motion";
import { useLang } from "@/context/LangContext";
import { InstallButton } from "@/components/InstallButton";

const KidsImage = ({ className = "" }) => {
  const [alt, setAlt] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setAlt((v) => !v), 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.3, duration: 1, ease: [0.22, 1, 0.36, 1] }} className={`relative ${className}`}>
      <div className="absolute inset-6 rounded-full bg-[#D97706]/20 blur-3xl" />
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative arch overflow-hidden border-[6px] border-white shadow-[0_40px_80px_-30px_rgba(28,25,23,0.45)] bg-[#FAF7F2] aspect-[848/1100]">
        <img src="/images/kids.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-top" data-testid="home-kids-image" />
        <img src="/images/yallahero-reading.jpg" alt="" data-testid="home-kids-image-alt"
          className={`absolute inset-0 w-full h-full object-contain bg-white transition-opacity duration-700 ${alt ? "opacity-100" : "opacity-0"}`} />
      </motion.div>
    </motion.div>
  );
};

const STEP_ICONS = [Languages, UserPlus, LibraryBig];

const Steps = ({ items }) => (
  <ol className="mt-5 lg:mt-6 [@media(min-width:768px)_and_(max-height:760px)]:mt-3 grid grid-cols-3 gap-2 sm:gap-3 max-w-2xl" data-testid="home-steps">
    {items.map((label, i) => {
      const Icon = STEP_ICONS[i];
      return (
        <motion.li key={label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.12, duration: 0.5 }}
          className="relative flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3 rounded-2xl bg-white/80 border border-[#E7DDCB] px-2 py-3 sm:px-4 [@media(min-width:768px)_and_(max-height:760px)]:py-2 text-center sm:text-start" data-testid={`home-step-${i + 1}`}>
          <span className="relative shrink-0 grid place-items-center w-10 h-10 rounded-full bg-[#8B3A2B]/10 text-[#8B3A2B]">
            <Icon className="w-5 h-5" />
            <span className="absolute -top-1.5 -end-1.5 grid place-items-center w-5 h-5 rounded-full bg-amber-400 text-[#1C1917] text-xs font-bold">{i + 1}</span>
          </span>
          <span className="text-xs sm:text-sm font-semibold text-[#1C1917] leading-snug">{label}</span>
        </motion.li>
      );
    })}
  </ol>
);

export default function Landing() {
  const { t, lang, isRtl } = useLang();
  const Arrow = isRtl ? ArrowLeft : ArrowRight;

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F2]" data-testid="landing-page">
      <Navbar />
      <Ticker key={lang} text={t.home.eyebrow} rtl={isRtl} />
      <main className="relative flex-1 overflow-hidden">
        <span className="absolute -top-24 -end-10 font-display text-[22rem] md:text-[30rem] leading-none text-[#8B3A2B]/[0.05] select-none pointer-events-none">يلّا</span>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 md:py-6 lg:py-8 [@media(min-width:768px)_and_(max-height:760px)]:py-4 grid md:grid-cols-12 gap-8 md:gap-8 lg:gap-14 items-start">
          <div className="md:col-span-7 md:pt-4 lg:pt-6 [@media(min-width:768px)_and_(max-height:760px)]:pt-0">
          <h1 className="font-display font-bold text-[#1C1917] text-3xl sm:text-4xl md:text-[clamp(1.75rem,6vh,2.25rem)] lg:text-[clamp(2rem,6.5vh,3.25rem)] leading-[1.2] lg:leading-[1.15]" data-testid="home-title">
            <MaskLines key={lang} lines={[t.home.title[0], <span className="text-[#8B3A2B]">{t.home.title[1]}</span>, ...(t.home.title[2] ? [<span className="text-[#047857]" data-testid="home-title-offline">{t.home.title[2]}</span>] : [])]} />
          </h1>
          {Array.isArray(t.home.steps) && <Steps items={t.home.steps} />}

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-5 lg:mt-6 [@media(min-width:768px)_and_(max-height:760px)]:mt-3 grid sm:grid-cols-2 gap-2.5 sm:gap-3 lg:gap-4 whitespace-nowrap text-sm lg:text-base">
            <Link to="/library" data-testid="home-enter-library-btn"
              className="group inline-flex items-center justify-between gap-3 rounded-full bg-[#1C1917] text-[#FAF7F2] px-4 lg:px-6 py-2.5 lg:py-3 border-2 border-[#1C1917] font-semibold hover:bg-[#8B3A2B] hover:border-[#8B3A2B] transition-colors">
              <span className="inline-flex items-center gap-2"><LibraryBig className="w-5 h-5" /> {t.home.library}</span>
              <Arrow className={`w-5 h-5 transition-transform ${isRtl ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"}`} />
            </Link>
            <InstallButton />
            <Link to="/register" data-testid="home-register-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#1C1917] px-4 lg:px-6 py-2.5 lg:py-3 font-semibold hover:bg-[#F2ECE1] transition-colors">
              <UserPlus className="w-5 h-5" /> {t.home.register}
            </Link>
            <Link to="/samples" data-testid="home-samples-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F2ECE1] border-2 border-[#F2ECE1] px-4 lg:px-6 py-2.5 lg:py-3 font-semibold text-[#8B3A2B] hover:border-[#8B3A2B] transition-colors">
              <BookOpenText className="w-5 h-5" /> {t.home.samples}
            </Link>
          </motion.div>
          </div>
          <div className="md:col-span-5"><KidsImage className="w-full max-w-[240px] md:max-w-[min(28rem,calc((100vh-9rem)*0.77))] mx-auto md:me-0 md:ms-auto" /></div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
